// frontend/src/pages/VerificationPage/VerificationPage.jsx

import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { UIContext } from '../../contexts/UIContext'; 
import { useTranslation, Trans } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Tabs, Tab, CircularProgress, Tooltip, Button, Typography, Paper, IconButton } from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TagIcon from '@mui/icons-material/Tag';
import DOMPurify from 'dompurify';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';

// Import MUI Icons
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import LinkIcon from '@mui/icons-material/Link';
import PublicIcon from '@mui/icons-material/Public';
import NotesIcon from '@mui/icons-material/Notes'
import PsychologyIcon from '@mui/icons-material/Psychology';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import VideocamIcon from '@mui/icons-material/Videocam';
import ImageIcon from '@mui/icons-material/Image';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getContentTypeIcon } from './contentTypeUtils';

import { getPlatformDisplayName } from '../../components/PlatformUtils/PlatformUtils.jsx';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import ErrorMessage from '../../components/ErrorMessage/ErrorMessage';
import StructuredExplanation from '../../components/StructuredExplanation/StructuredExplanation';
import HeatmapModal from '../../components/HeatmapModal/HeatmapModal';
import HistoryTextInspectionModal from '../../components/TextInspectionModal/HistoryTextInspectionModal';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';
import ScoreDonut from './ScoreDonut';
import LicenseModal from '../../components/AdCreativeTools/LicenseModal';
import './VerificationPage.css';

// --- Animation Variants ---
const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.07 } } };
const itemVariants = { hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1, transition: { duration: 0.4, ease: 'easeOut' } } };

// --- HELPER FUNCTIONS ---
const getTaskDisplayName = (taskKey, t) => t(`verificationPage.analysisTasks.${taskKey}`, { defaultValue: taskKey });
const isValidPreview = (src) => src && (src.startsWith('data:image') || src.startsWith('http'));

// Standard timestamp formatter (Keep this for analysis timestamps)
const formatDate = (isoString, language) => {
    if (!isoString) return 'N/A';
    try {
        const date = new Date(isoString);
        return date.toLocaleString(language, { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric', 
            hour: 'numeric', 
            minute: '2-digit'
        });
    } catch (e) { return 'Error'; }
};

// --- SAFE EXPIRY FORMATTER ---
// Specific formatter for License Expiration to prevent rounding up to next day
const formatExpiryDate = (isoString, language) => {
    if (!isoString) return '';
    try {
        // 1. Isolate YYYY-MM-DD (Ignore time/microseconds)
        const datePart = isoString.split('T')[0];
        if (!datePart) return '';

        // 2. Parse components
        const [year, month, day] = datePart.split('-').map(Number);

        // 3. Create Date in UTC
        const date = new Date(Date.UTC(year, month - 1, day));

        // 4. Format in UTC to prevent local timezone shifts
        return date.toLocaleDateString(language, { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric',
            timeZone: 'UTC' 
        });
    } catch (e) {
        return '';
    }
};

// --- CLEAN MARKDOWN ---
// This applies the same normalization logic as ExplanationModal
const cleanMarkdown = (text) => {
    if (typeof text !== 'string') return text;

    let cleanText = text;
    // 1. Basic cleanup: Remove escaped asterisks
    cleanText = cleanText.replace(/\\\*/g, ''); 

    // 2. AGGRESSIVE HEADER NORMALIZATION
    // Pattern: Start -> Bullet -> Header Text (no colons) -> Colon -> Rest of Line
    cleanText = cleanText.replace(
        // /(^|\n)([\*\-]\s+)([^:\n]+):\s*(.*)/g, // VETRA_AI_IGNORE_LINE
        /(^|\n)([-*]\s+)([^:\n]+):\s*(.*)/g,
        (match, newline, bullet, headerContent, restOfLine) => {
            // 1. Clean the header: Remove existing asterisks, trim whitespace
            const cleanHeader = headerContent.replace(/\*/g, '').trim();
            
            // 2. Clean the body: Remove leading asterisks that might be left over (e.g. "** The body")
            let cleanBody = restOfLine.trim();
            // Remove up to 2 leading asterisks from body if they exist (fixing the "** Body" issue)
            cleanBody = cleanBody.replace(/^\*+\s*/, '');

            // 3. Reconstruct: Bullet + **Header:** + Space + Body
            return `${newline}${bullet}**${cleanHeader}:** ${cleanBody}`;
        }
    );
    return cleanText;
};

// --- SKELETON LOADER ---
const VerificationSkeleton = () => (
    <div className="verification-container skeleton-active">
        <div className="report-header">
            {/* Title */}
            <div className="skeleton skeleton-title"></div>
            {/* Tabs */}
            <div className="skeleton skeleton-tabs"></div>
        </div>
        
        <div className="report-layout-grid">
            <div className="top-row-layout">
                {/* LEFT COLUMN */}
                <div className="left-column-stack">
                    
                    {/* Content Preview Card */}
                    <div className="content-preview-card skeleton-card">
                        <div className="skeleton skeleton-subtitle-compact"></div>
                        
                        {/* Media Placeholder */}
                        <div className="skeleton skeleton-media-compact"></div>
                        
                        {/* --- Metadata Skeleton --- */}
                        <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            {/* Summary Text Lines */}
                            <div className="skeleton skeleton-text-line-compact" style={{ height: '0.9rem', width: '90%' }}></div>
                            <div className="skeleton skeleton-text-line-compact" style={{ height: '0.9rem', width: '60%' }}></div>
                            
                            {/* Chips Row */}
                            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                                <div className="skeleton" style={{ width: '80px', height: '24px', borderRadius: '16px' }}></div>
                                <div className="skeleton" style={{ width: '120px', height: '24px', borderRadius: '16px' }}></div>
                            </div>
                        </div>
                    </div>

                    {/* Context Card */}
                    <div className="context-card skeleton-card">
                        <div className="skeleton skeleton-subtitle-compact"></div>
                        <div className="skeleton skeleton-text-line-compact" style={{ width: '40%', marginBottom: '0.5rem' }}></div>
                        <div className="skeleton skeleton-text-line-compact" style={{ width: '70%', height: '1rem' }}></div>
                    </div>

                    {/* Share Card */}
                    <div className="share-card skeleton-card">
                        <div className="skeleton skeleton-subtitle-compact"></div>
                        <div className="skeleton skeleton-qr-compact"></div>
                        <div className="skeleton skeleton-button-compact"></div>
                    </div>
                </div>

                {/* RIGHT COLUMN */}
                <div className="right-column-fill">
                    <div className="analysis-summary-card skeleton-card">
                        <div className="skeleton skeleton-donut-compact"></div>
                        <div className="skeleton-metadata-grid-compact">
                            {[...Array(6)].map((_, i) => (<div key={i} className="skeleton skeleton-text-line-compact"></div>))}
                        </div>
                    </div>
                </div>
            </div>

            {/* BOTTOM ROW */}
            <div className="bottom-row-layout">
                <div className="explanation-card skeleton-card">
                    <div className="skeleton skeleton-subtitle-compact"></div>
                    <div className="skeleton skeleton-text-line-compact" style={{ height: '0.9rem', width: '90%' }}></div>
                    <div className="skeleton skeleton-text-line-compact" style={{ height: '0.9rem', width: '80%' }}></div>
                    <div className="skeleton skeleton-text-line-compact" style={{ height: '0.9rem', width: '85%' }}></div>
                </div>
            </div>
        </div>
    </div>
);

function VerificationPage() {
    const { t, i18n } = useTranslation();
    const { user, fetchWithAuth, isSessionLoading } = useContext(AuthContext);
    const { openDocViewer } = useContext(UIContext); 
    const location = useLocation();
    const navigate = useNavigate();
    const [copySuccess, setCopySuccess] = useState(false);
    const [copiedItemId, setCopiedItemId] = useState(null);
    const [isHeatmapModalOpen, setIsHeatmapModalOpen] = useState(false);
    const [heatmapModalData, setHeatmapModalData] = useState({});
    const [isTextInspectionModalOpen, setIsTextInspectionModalOpen] = useState(false);
    const [textInspectionModalData, setTextInspectionModalData] = useState({});
    const [isQrLoading, setIsQrLoading] = useState(true);
    const [translatedDescription, setTranslatedDescription] = useState(null);
    const [translatedExplanation, setTranslatedExplanation] = useState(null);
    const [isCheckingCache, setIsCheckingCache] = useState(false);
    const [isCheckingDescriptionCache, setIsCheckingDescriptionCache] = useState(false);
    const [isDownloadingQr, setIsDownloadingQr] = useState(false);
    const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
    const [isPayingLicense, setIsPayingLicense] = useState(false);
    const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    // TanStack Query Client & Extracted URL Params
    const queryClient = useQueryClient();
    const searchParams = new URLSearchParams(location.search);
    const exactHash = searchParams.get('id');
    const queryString = location.search.replace('?', '');

    // Main Verification Data Query (Critical Path)
    const { 
        data: verifyData, 
        isLoading: isVerifyLoading, 
        error: verifyErrorObj 
    } = useQuery({
        queryKey: ['verification', exactHash, queryString],
        queryFn: async () => {
            if (!exactHash) throw new Error(t('verificationPage.errors.incompleteLink'));
            const res = await fetch(`/api/verify/all/${exactHash}?${queryString}`, { credentials: 'include' });
            if (!res.ok) throw new Error(t('verificationPage.errors.loadFailed'));
            return res.json();
        },
        enabled: !!exactHash && !isSessionLoading,
        retry: false // Don't keep retrying if the URL is a 404
    });

    // Tasks Config Query (Critical Path)
    const { data: tasksData } = useQuery({
        queryKey: ['tasksConfig'],
        queryFn: async () => {
            const res = await fetch('/api/config/tasks');
            if (!res.ok) return null;
            return res.json();
        },
        staleTime: Infinity, // Tasks config rarely changes
    });

    // Pricing & Rates Queries (Background / Non-critical)
    const { data: pricingConfig } = useQuery({
        queryKey: ['pricingConfig'],
        queryFn: async () => {
            const res = await fetchWithAuth('/api/config/billing');
            return res.ok ? res.json() : null;
        },
        enabled: !!user && !isSessionLoading,
        staleTime: 1000 * 60 * 30, // 30 minutes cache
    });

    const { data: ratesData } = useQuery({
        queryKey: ['currencyRates'],
        queryFn: async () => {
            const res = await fetchWithAuth('/api/user/billing?limit=1');
            return res.ok ? res.json() : null;
        },
        enabled: !!user && !isSessionLoading,
        staleTime: 1000 * 60 * 30, // 30 minutes cache
    });

    // Derive the variables your JSX expects
    const isLoading = isSessionLoading || isVerifyLoading;
    const error = (!exactHash ? t('verificationPage.errors.incompleteLink') : verifyErrorObj?.message) || null;
    
    const contentDetails = verifyData?.content_details || null;
    const analysisResults = useMemo(() => verifyData?.analysis_results || {}, [verifyData?.analysis_results]);
    const licenseInfo = verifyData?.license_info || null;
    const creatorPrefs = verifyData?.creator_prefs || {};
    
    const tasksConfig = tasksData?.tasks || null;
    const masterTaskOrder = tasksConfig ? Object.keys(tasksConfig) : [];
    const currencyRates = ratesData?.currency_rates || { USD: 1 };

    // --- Parse Options from URL ---
    const refParam = searchParams.get('ref');
    const showRegionsParam = searchParams.get('regions');
    const showExplanationParam = searchParams.get('explanation');
    const viewParam = searchParams.get('view');

    const shouldShowRegions = showRegionsParam !== 'false';
    const shouldShowExplanation = showExplanationParam !== 'false';
    const isSimulatingPublic = viewParam === 'public';

    const particlesInit = useCallback(async (engine) => { await loadSlim(engine); }, []);
    const particlesConfig = { "background": { "color": { "value": "transparent" } }, "fpsLimit": 120, "interactivity": { "events": { "onHover": { "enable": true, "mode": "repulse" }, "resize": true }, "modes": { "repulse": { "distance": 100, "duration": 0.4 } } }, "particles": { "color": { "value": "#ffffff" }, "links": { "color": "#4fc3f7", "distance": 150, "enable": true, "opacity": 0.4, "width": 1 }, "move": { "direction": "none", "enable": true, "outModes": { "default": "bounce" }, "random": false, "speed": 1, "straight": false }, "number": { "density": { "enable": true, "area": 800 }, "value": 80 }, "opacity": { "value": 0.5 }, "shape": { "type": "circle" }, "size": { "value": { "min": 1, "max": 3 } } }, "detectRetina": true };
    const headerProps = { showDataFlowPreview: false, hideUserActions: true, disableLogoLink: true, showLanguageSwitcher: true };
    const footerProps = { showFaqButton: false, hideNavLinks: true };

    // --- View Toggling Handlers ---
    const togglePublicView = (enable) => {
        const newParams = new URLSearchParams(location.search);
        if (enable) {
            newParams.set('view', 'public');
        } else {
            newParams.delete('view');
        }
        // Use navigate to update URL without full page reload
        // This triggers the useEffect above
        navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
    };

    const licenseMutation = useMutation({
        mutationFn: async (durationDays) => {
            const response = await fetchWithAuth('/api/license/activate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    exact_hash: contentDetails.exact_hash,
                    content_type: contentDetails.content_type,
                    duration_days: durationDays
                })
            });
            if (!response.ok) throw new Error("Activation failed");
            return durationDays;
        },
        onMutate: () => setIsPayingLicense(true),
        onSuccess: (durationDays) => {
            // Optimistically update the exact cache query to show it's paid instantly!
            queryClient.setQueryData(['verification', exactHash, queryString], (oldData) => {
                if (!oldData) return oldData;
                const currentExp = oldData.license_info?.expires_at ? new Date(oldData.license_info.expires_at) : new Date();
                currentExp.setDate(currentExp.getDate() + durationDays);
                return {
                    ...oldData,
                    license_info: {
                        ...oldData.license_info,
                        status: 'active',
                        expires_at: currentExp.toISOString(),
                        owner_view: true 
                    }
                };
            });
            setIsLicenseModalOpen(false);
        },
        onError: (err) => {
            console.error("Payment failed", err);
            alert("Payment failed. Please try again.");
        },
        onSettled: () => setIsPayingLicense(false)
    });

    const handleLicensePayment = (durationDays) => {
        licenseMutation.mutate(durationDays);
    };

    // ========================================================================
    // --- ROBUST META-TASK CHECKER ---
    // Uses tasksConfig from the backend as the single source of truth.
    // ========================================================================
    const checkIsMetaTask = useCallback((taskKey) => {
        if (!taskKey) return false;
        // Primary Check: Configuration mapping
        if (tasksConfig && tasksConfig[taskKey]) {
            return tasksConfig[taskKey].is_meta_task === true;
        }
        // Fallback Check: Safely examine the explanation shape if config hasn't loaded
        const res = analysisResults[taskKey];
        if (res) {
            const exp = res.explanation !== undefined ? res.explanation : res.explanation_text;
            return exp !== null && exp !== "" && typeof exp === 'object';
        }
        return false;
    }, [tasksConfig, analysisResults]);

    // --- DYNAMIC TAB ORDERING & FILTERING ---
    const resultTasksInOrder = [];
    const metaTasks = [];
    const baseTasks = [];

    // 1. Categorize results
    Object.keys(analysisResults).forEach(key => {
        const isMeta = checkIsMetaTask(key);
        
        if (isMeta) {
            // --- Respect the creator's preferences ---
            const enabledMetaTasks = creatorPrefs?.enabled_meta_tasks;
            if (Array.isArray(enabledMetaTasks) && !enabledMetaTasks.includes(key)) {
                return; // Hide this meta-task if the creator turned it off!
            }
            metaTasks.push(key);
        } else {
            baseTasks.push(key);
        }
    });

    // 2. Order base tasks according to config
    baseTasks.sort((a, b) => {
        if (!masterTaskOrder) return 0;
        const indexA = masterTaskOrder.indexOf(a);
        const indexB = masterTaskOrder.indexOf(b);
        if (indexA === -1 && indexB === -1) return 0;
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
    });

    // 3. Combine: Meta tasks first
    resultTasksInOrder.push(...metaTasks);
    resultTasksInOrder.push(...baseTasks);

    const activeTaskKey = resultTasksInOrder.length > 0 ? resultTasksInOrder[activeTab] : null;
    const activeResultData = activeTaskKey ? analysisResults[activeTaskKey] : null;
    const rawExplanation = activeResultData?.explanation || activeResultData?.explanation_text || "";
    
    // Define if the current active tab is a meta task
    const isMetaTask = rawExplanation !== null && typeof rawExplanation === 'object';
    // --- METADATA PRIORITY LOGIC ---
    // This ensures we always show a consistent description/flag set
    const getPrioritizedMetadata = () => {
        // 1. Always prioritize AI Detection metadata if available.
        if (analysisResults['ai_detection']?.content_metadata) {
            return analysisResults['ai_detection'].content_metadata;
        }

        // 2. Fallback to Fake News metadata.
        if (analysisResults['fake_news_detection']?.content_metadata) {
            return analysisResults['fake_news_detection'].content_metadata;
        }

        // 3. Fallback to active result
        return activeResultData?.content_metadata || null;
    };
    const activeMetadata = getPrioritizedMetadata();

    // --- HELPER: Check if a specific task result has valid regions ---
    const checkRegionsForTask = (taskData) => {
        return taskData && (
            (contentDetails?.content_type === 'image' && taskData.manipulation_regions?.length > 0) ||
            (contentDetails?.content_type === 'text' && taskData.manipulation_regions?.length > 0) ||
            (contentDetails?.content_type === 'video' && taskData.manipulation_regions?.boxes?.length > 0)
        );
    };

    // --- Auto-Check Cache for Description Translation ---
    useEffect(() => {
        setTranslatedDescription(null);
        
        // Skip if English, no summary, or we already have it
        if (i18n.language.startsWith('en') || !activeMetadata?.summary) {
            return;
        }

        const checkDescriptionCache = async () => {
            setIsCheckingDescriptionCache(true); // <--- START LOADING
            try {
                const response = await fetch(`/api/translate/cached?text=${encodeURIComponent(activeMetadata.summary)}&target_language=${i18n.language}`);
                if (response.ok) {
                    const data = await response.json();
                    if (data.translated_text) {
                        setTranslatedDescription(data.translated_text);
                    }
                }
            } catch (err) {
                console.error("Description cache check failed:", err);
            } finally {
                setIsCheckingDescriptionCache(false); // <--- STOP LOADING
            }
        };
        checkDescriptionCache();
    }, [i18n.language, activeMetadata, setIsCheckingDescriptionCache]);

    useEffect(() => {
        setTranslatedExplanation(null);
        setIsCheckingCache(false);
        if (i18n.language.startsWith('en') || isMetaTask || !rawExplanation || !shouldShowExplanation) {
            return;
        }
        const checkCache = async () => {
            setIsCheckingCache(true);
            try {
                const response = await fetch(`/api/translate/cached?text=${encodeURIComponent(rawExplanation)}&target_language=${i18n.language}`);
                if (response.ok) {
                    const data = await response.json();
                    if (data.translated_text) {
                        setTranslatedExplanation(data.translated_text);
                    }
                }
            } catch (err) {
                console.error("Public cache check failed:", err);
            } finally {
                setIsCheckingCache(false);
            }
        };
        checkCache();
    }, [activeTab, activeResultData, i18n.language, rawExplanation, activeTaskKey, shouldShowExplanation, isMetaTask]);

    const getDynamicLikelihoodLabel = (taskKey) => {
        switch (taskKey) {
            case 'ai_detection': return t('verificationPage.dynamicLabels.likelihoodAi');
            case 'fake_news_detection': return t('verificationPage.dynamicLabels.likelihoodMisinformation');
            default: return t('verificationPage.dynamicLabels.riskDetected');
        }
    };

    const handleTabChange = (event, newValue) => { setActiveTab(newValue); };
    const handleCopyLink = () => { 
        // Use the shortlink API route so it's clean and perfectly preserves context!
        const shortUrl = `${window.location.origin}/api/v/${contentDetails.vetra_id}`;
        
        navigator.clipboard.writeText(shortUrl).then(() => { 
            setCopySuccess(true); 
            setTimeout(() => setCopySuccess(false), 2000); 
        }); 
    };
    // const handleCopyLink = () => { navigator.clipboard.writeText(window.location.href).then(() => { setCopySuccess(true); setTimeout(() => setCopySuccess(false), 2000); }); };
    const handleDownloadQr = async () => {
        if (!qrCodeUrl || isDownloadingQr) return;
        setIsDownloadingQr(true); // Start loading
        try {
            const response = await fetch(qrCodeUrl);
            const blob = await response.blob();
            
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            // Extract short hash for filename or fallback
            const shortHash = contentDetails?.exact_hash ? contentDetails.exact_hash.substring(0, 8) : 'code';
            a.download = `verification-qr-${shortHash}.png`;
            
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            // --- DELAY CLEANUP UNTIL AFTER DOWNLOAD COMPLETES ---
            // Don't revoke immediately. Let the browser finish writing the file.
            // We'll clean up after the component unmounts or after a safe delay.
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
            }, 60000); // 60 seconds - safe margin for large files

        } catch (err) {
            console.error("QR Download failed", err);
        } finally {
            setIsDownloadingQr(false); // Stop loading
        }
    };

    const handleInspectClick = (targetTaskKey) => {
        const taskName = getTaskDisplayName(targetTaskKey, t);
        // Get result data for the specific task being inspected (handle "Vetra Score" sub-tasks)
        const targetResultData = analysisResults[targetTaskKey];
        const manipulationRegions = targetResultData?.manipulation_regions;

        if (!manipulationRegions) return;

        if (contentDetails.content_type === 'image') {
            setHeatmapModalData({ imageSrc: contentDetails.content_preview, regions: manipulationRegions, taskName, score: targetResultData.score, mode: 'history' });
            setIsHeatmapModalOpen(true);
        } else if (contentDetails.content_type === 'video' && manipulationRegions.boxes) {
            const videoNote = t('heatmap.footerNoteVideo', { time: manipulationRegions.key_frame_time.toFixed(2) });
            setHeatmapModalData({ imageSrc: manipulationRegions.key_frame_thumbnail, regions: manipulationRegions.boxes, taskName, score: targetResultData.score, mode: 'history', footerNote: videoNote });
            setIsHeatmapModalOpen(true);
        } else if (contentDetails.content_type === 'text') {
            setTextInspectionModalData({ regions: manipulationRegions, taskName, score: targetResultData.score });
            setIsTextInspectionModalOpen(true);
        }
    };

    if (error) return <div className="verification-page-wrapper"><Header {...headerProps} /><main className="verification-page-main-content"><ErrorMessage message={error} /></main><Footer {...footerProps} /></div>;

    const rawContext = activeResultData?.user_context
        || analysisResults['ai_detection']?.user_context
        || analysisResults['fake_news_detection']?.user_context
        || {};
    const hasContext = rawContext.source_url || rawContext.platform || rawContext.user_notes;

    // --- Public URL Logic for Metadata Panel ---
    const publicSourceUrl = activeResultData?.original_source_url
    const truncateUrl = (url, maxLen = 38) => {
        if (!url) return '';
        if (url.length <= maxLen) return url;
        return url.substring(0, maxLen) + '...';
    };

    const metadataItems = activeResultData && contentDetails ? [
        { 
            icon: <TagIcon />, 
            label: t('verificationPage.contentInfoSection.vetraId'), 
            value: contentDetails.vetra_id || 'Generating...', 
            isHash: false,
            copyable: true,
            copyText: contentDetails.vetra_id,
            copyTooltip: t('verificationPage.tooltips.copyVetraId'),
            infoTooltip: t('verificationPage.tooltips.vetraIdTooltip')
        },
        {
            icon: <LinkIcon />,
            label: t('verificationPage.contentInfoSection.publicSourceUrl'),
            value: publicSourceUrl ? truncateUrl(publicSourceUrl, 38) : t('verificationPage.contentInfoSection.notAvailable'),
            isHash: false,
            copyable: !!publicSourceUrl,
            copyText: publicSourceUrl,
            copyTooltip: t('snippetArea.copy'),
            dimmed: !publicSourceUrl,
            isLink: !!publicSourceUrl
        },
        { icon: getContentTypeIcon(contentDetails.content_type), label: t('verificationPage.contentInfoSection.contentType'), value: t(`verificationPage.contentInfoSection.contentTypes.${contentDetails.content_type}`) },
        { icon: <VerifiedUserOutlinedIcon />, label: getDynamicLikelihoodLabel(activeTaskKey), value: t(`verificationPage.dynamicValues.${(activeResultData?.likelihood || 'na').toLowerCase()}`, { defaultValue: activeResultData?.likelihood || 'N/A' }) },
        { icon: <CalendarTodayIcon />, label: t('verificationPage.contentInfoSection.analyzedOn'), value: formatDate(activeResultData.analysis_timestamp, i18n.language) },
        { icon: <PsychologyIcon />, label: t('verificationPage.analysisSection.modelId'), value: activeResultData?.model_id || 'N/A' },
        { icon: <ReceiptLongOutlinedIcon />, label: t('verificationPage.analysisSection.recordId'), value: typeof activeResultData?.analysis_id === 'string' && activeResultData.analysis_id.startsWith('Derived from') ? t('verificationPage.dynamicValues.derivedFrom', { ids: activeResultData.analysis_id.match(/\d+/g).join(' & #') }) : activeResultData?.analysis_id || 'N/A' },
        { icon: <FingerprintIcon />, label: t('verificationPage.contentInfoSection.fingerprintExact'), value: contentDetails.exact_hash, isHash: true },
    ] : [];

    const contextHash = new URLSearchParams(location.search).get('ctx');
    const tasksParam = new URLSearchParams(location.search).getAll('context');

    let qrCodeUrl = '';
    if (contentDetails) {
        qrCodeUrl = `/api/qr/${contentDetails.exact_hash}`;
        const params = [];
        // Determine the Attribution ID (Public ID)
        // Priority 1: Use 'ref' from the URL (if a visitor is viewing a shared link)
        // Priority 2: Use 'user.public_id' (if the creator is viewing their own history)
        const attributionId = refParam || user?.public_id;
        if (attributionId) {
            params.push(`ref=${attributionId}`);
        }
        if (contextHash) params.push(`ctx=${contextHash}`);
        if (tasksParam.length > 0) {
            tasksParam.forEach(task => params.push(`tasks=${task}`));
        }
        if (shouldShowRegions !== true) params.push(`regions=${shouldShowRegions}`);
        if (shouldShowExplanation !== true) params.push(`explanation=${shouldShowExplanation}`);

        if (params.length > 0) {
            qrCodeUrl += `?${params.join('&')}`;
        }
    }

    // --- DETERMINE CARD TITLE ---
    let cardTitle = "";
    if (shouldShowExplanation && shouldShowRegions) {
        cardTitle = t('verificationPage.analysisSection.combinedTitle');
    } else if (shouldShowExplanation) {
        cardTitle = t('verificationPage.analysisSection.explanation');
    } else if (shouldShowRegions) {
        cardTitle = t('verificationPage.analysisSection.inspectTitle');
    }

    // --- Add Task Name Suffix (Corrected Logic) ---
    if (activeTaskKey !== 'digital_integrity_score') {
        const taskName = getTaskDisplayName(activeTaskKey, t);
        cardTitle = `${cardTitle} (${taskName})`;
    }

    // --- INSPECT BUTTON COMPONENT ---
    const InspectButton = ({ taskKey }) => {
        const targetResult = analysisResults[taskKey];
        const taskHasRegions = checkRegionsForTask(targetResult);

        // Check if the feature was enabled in the DB.
        // In the DB: NULL means disabled. Empty Array [] means enabled but none found.
        const wasRegionsEnabled = targetResult?.manipulation_regions !== null && targetResult?.manipulation_regions !== undefined;

        // Dynamic Button Text
        const btnText = `${t('resultDisplay.inspectContent')} ${getTaskDisplayName(taskKey, t)}`;

        let tooltipText;
        if (!wasRegionsEnabled) {
            // Case 1: User didn't ask for regions
            tooltipText = t('verificationPage.tooltips.regionsDisabled');
        } else if (!taskHasRegions) {
            // Case 2: User asked, but AI found nothing (Clean)
            tooltipText = t('resultDisplay.noRegionsFlagged', { taskName: getTaskDisplayName(taskKey, t) });
        } else {
            // Case 3: Regions found
            tooltipText = t(`verificationPage.inspectTooltips.${taskKey}`, { defaultValue: t('resultDisplay.inspectDetails') });
        }

        return (
            <Tooltip
                title={tooltipText}
                arrow
                PopperProps={{ disablePortal: true, modifiers: [{ name: 'offset', options: { offset: [0, -8] } }] }}
            >
                <span className="inspect-content-button-wrapper" style={{ marginRight: '10px' }}>
                    <Button
                        variant="outlined"
                        startIcon={<SearchIcon />}
                        onClick={() => handleInspectClick(taskKey)}
                        // Disable if NO regions found (applies to both Case 1 and Case 2)
                        disabled={!taskHasRegions}
                        className="inspect-content-button"
                        sx={{ marginTop: '0 !important' }}
                    >
                        {btnText}
                    </Button>
                </span>
            </Tooltip>
        );
    };

    // --- Prepare Sub-Scores for Donut ---
    const subScores = {};
    Object.keys(analysisResults).forEach(key => {
        const res = analysisResults[key];
        const isMeta = checkIsMetaTask(key);
        if (!isMeta) {
            subScores[key] = res?.score;
        }
    });

    // Determine Banner State
    const isOwner = licenseInfo?.is_owner;
    
    // 1. Base active status from backend
    // We use 'let' because we might revoke it locally
    let isActive = licenseInfo?.status === 'active';

    // 2. SHIELD BYPASS & TIME BOMB VALIDATION
    const isShieldBypass = licenseInfo?.source_bypass === true;
    const isManualBypass = licenseInfo?.manual_bypass === true;

    // --- MANUAL BYPASS FOR SPECIFIC HASH ---
    // VETRA_AI_IGNORE_NEXT
    if (contentDetails?.exact_hash === '6584d82f0609a8301a48afc5397b704042edf2ba4128f8057f6ddf09d847b8fb') {
        isActive = true;
    }

    // 3. LOCK LOGIC
    const isExpired = licenseInfo?.expired_shield === true;
    // Locked if: Not Loading AND Info Exists AND Not Owner View AND Not Active (Computed)
    const isContentLocked = !isLoading && licenseInfo && !licenseInfo.owner_view && !isActive; 

    // 4. SHARE CARD VISIBILITY LOGIC:
    // - Hide if it's a Shield/CPM Bypass (Prevents free access to QR via Ad view)
    // - Hide if License is Inactive, UNLESS it is the Owner viewing (non-simulated)
    // - Show if Standard Active License
    const shouldShowShareCard = !isShieldBypass && (isActive || (isOwner && !isSimulatingPublic));

    // --- DYNAMIC LOCK MESSAGE LOGIC ---
    let lockTitle = t('verificationPage.inactive.title');
    let lockDescription = t('verificationPage.inactive.description');
    
    // Customize message if it is a Shield Time Bomb expiry
    if (isShieldBypass && isExpired) {
        lockTitle = t('verificationPage.inactive.lockTitle');
        lockDescription = t('verificationPage.inactive.lockDescription');
    }

    // --- PREPARE DYNAMIC TEXT FOR INFO MODAL ---
    const localizedContentType = t(`verificationPage.contentInfoSection.contentTypes.${contentDetails?.content_type}`, { defaultValue: contentDetails?.content_type }).toLowerCase();
    const activeBaseTaskNames = baseTasks.map(tKey => getTaskDisplayName(tKey, t)).join(', ');
    const activeMetaTaskNames = metaTasks.map(tKey => getTaskDisplayName(tKey, t)).join(', ');

    return (
        <div className="verification-page-wrapper">
            <Particles id="tsparticles-verify" init={particlesInit} options={particlesConfig} />
            <Helmet>
                <title>{t('verificationPage.helmet.title')}</title>
                <meta name="description" content={t('verificationPage.helmet.description')} /><meta name="robots" content="noindex, nofollow" />
            </Helmet>
            <Header {...headerProps} />

            {/* =====================================================================================
                BANNERS
                Using Box with styles matching LicenseModal (Alert styles)
            ===================================================================================== */}
            
            {/* 1. PUBLIC SIMULATION BANNER (Blue/Info) */}
            {isSimulatingPublic && (
                <Box sx={{ 
                    backgroundColor: '#e3f2fd', // Light Blue
                    color: '#0d47a1',           // Dark Blue
                    p: 1.5, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    gap: 2, 
                    zIndex: 5,
                    borderBottom: '1px solid #90caf9'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <VisibilityIcon fontSize="small" />
                        <Typography variant="body2" fontWeight="600">
                            {t('verificationPage.inactive.previewBanner')}
                        </Typography>
                    </Box>
                    <Button 
                        variant="outlined" 
                        size="small" 
                        color="primary" // Inherits blue
                        onClick={() => togglePublicView(false)}
                        startIcon={<CloseIcon />}
                        sx={{ 
                            backgroundColor: 'white',
                            borderColor: '#0d47a1',
                            '&:hover': { backgroundColor: '#f5f5f5' } 
                        }}
                    >
                        {t('verificationPage.inactive.previewReturn')}
                    </Button>
                </Box>
            )}

            {/* 2. OWNER ACTIONS BANNER (Only if Owner and NOT simulating) */}
            {isOwner && !isSimulatingPublic && !isShieldBypass && !isManualBypass && (
                <Box sx={{ 
                    backgroundColor: isActive ? '#e8f5e9' : '#fff3e0', // Green vs Orange
                    color: isActive ? '#1b5e20' : '#e65100',           // Dark Green vs Dark Orange
                    p: 1.5, 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    gap: 2, 
                    zIndex: 5,
                    borderBottom: `1px solid ${isActive ? '#81c784' : '#ffb74d'}`
                }}>
                    {/* Message Area */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isActive ? <InfoOutlinedIcon /> : <WarningAmberIcon />}
                        <Typography variant="body2" fontWeight="600">
                            {t('verificationPage.inactive.ownerView')}&nbsp;-&nbsp; 
                            {isActive 
                                ? <Trans i18nKey="verificationPage.inactive.licenseActive" values={{ date: formatExpiryDate(licenseInfo.expires_at, i18n.language) }} />
                                : t('verificationPage.inactive.licenseInactive')
                            }
                        </Typography>
                    </Box>

                    {/* Actions Area */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {/* Primary Action: Activate/Extend */}
                        <Button 
                            variant="contained" 
                            size="small"
                            onClick={() => setIsLicenseModalOpen(true)}
                            sx={{
                                backgroundColor: isActive ? '#2e7d32' : '#e65100',
                                color: 'white',
                                '&:hover': { backgroundColor: isActive ? '#1b5e20' : '#ef6c00' }
                            }}
                        >
                            {isActive ? t('verificationPage.inactive.ownerViewExtend') : t('verificationPage.inactive.ownerViewActivate')}
                        </Button>

                        {/* Secondary Action: Preview Public */}
                        <Button 
                            variant="outlined" 
                            size="small"
                            onClick={() => togglePublicView(true)}
                            startIcon={<VisibilityIcon />}
                            sx={{
                                borderColor: isActive ? '#2e7d32' : '#e65100',
                                color: isActive ? '#2e7d32' : '#e65100',
                                backgroundColor: 'transparent',
                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' }
                            }}
                        >
                            {t('verificationPage.inactive.ownerViewPreview')}
                        </Button>
                    </Box>
                </Box>
            )}

            {/* 3. INACTIVE LICENSE OVERLAY MODAL */}
                {isContentLocked && (
                    <div style={{
                        position: 'fixed',
                        top: 0, 
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 100, // Lower than Header (usually 1100), higher than content
                        pointerEvents: 'none', // Allow clicks to pass through transparent areas (like to the Header)
                    }}>
                            <Paper 
                            elevation={6}
                            sx={{ 
                                p: 5, 
                                backgroundColor: 'var(--primary-color)',
                                border: (isShieldBypass && isExpired) ? '3px solid #d32f2f' : '3px solid #ed6c02',
                                borderRadius: '12px',
                                maxWidth: '500px',
                                width: '90%', 
                                textAlign: 'center',
                                pointerEvents: 'auto', // Re-enable clicks specifically for the modal card
                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
                            }}
                        >
                            {isShieldBypass && isExpired ? (
                                <HistoryToggleOffIcon sx={{ fontSize: 60, color: '#d32f2f', mb: 2 }} /> 
                            ) : (
                                <HourglassEmptyIcon sx={{ fontSize: 60, color: '#ed6c02', mb: 2 }} />
                            )}
                            <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold', mb: 2 }}>
                                {lockTitle}
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#ccc', mb: 3, whiteSpace: 'pre-line' }}>
                                {lockDescription}
                            </Typography>
                            {isShieldBypass && isExpired ? (
                                ""
                            ) : (
                                <Button 
                                    variant="contained" 
                                    color="secondary"
                                    onClick={() => openDocViewer('user_guides/understanding_the_verification_page.md')}
                                    sx={{
                                        backgroundColor: '#ed6c02', // Force Orange background
                                        '&:hover': { backgroundColor: '#e65100' }
                                    }}
                                >
                                    {t('verificationPage.inactive.button')}
                                </Button>
                            )}
                        </Paper>
                    </div>
                )}

            <main className="verification-page-main-content">
                {isLoading ? (
                    <div className="loading-container-wrapper">
                        {/* Skeleton is blurred by the overlay via CSS backdrop-filter */}
                        <VerificationSkeleton />
                        <div className="loading-overlay">
                            <div className="loading-overlay-content">
                                <CircularProgress 
                                    className="loading-spinner" 
                                    size={60} 
                                    aria-label={t('verificationPage.loadingMessage', { defaultValue: 'Loading' })} 
                                />
                                <Typography className="loading-text">{t('verificationPage.loadingMessage')}</Typography>
                            </div>
                        </div>
                    </div>
                ) : (
                    <motion.div 
                        className="verification-container" 
                        variants={containerVariants} 
                        initial="hidden" 
                        animate="visible"
                        style={{
                            // Apply blur directly to content so it doesn't affect Header or Modal
                            filter: isContentLocked ? 'blur(15px)' : 'none',
                            // Prevent interaction with the blurred content
                            pointerEvents: isContentLocked ? 'none' : 'auto',
                            userSelect: isContentLocked ? 'none' : 'auto',
                            opacity: isContentLocked ? 0.6 : 1,
                            transition: 'all 0.3s ease'
                        }}
                    >
                        <motion.div variants={itemVariants} className="report-header">
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                <h1 style={{ margin: 0 }}>{t('verificationPage.mainTitle')}</h1>
                                
                                {/* INFO BUTTON WITH GENTLE PULSE */}
                                <Tooltip title={t('verificationPage.infoModal.tooltip')} arrow placement="top">
                                    <IconButton 
                                        onClick={() => setIsInfoModalOpen(true)} 
                                        size="small" 
                                        className="info-pulse-button"
                                        sx={{ 
                                            border: '1px solid rgba(255,255,255,0.3)', 
                                            color: 'rgba(255,255,255,0.8)', 
                                            backgroundColor: 'rgba(255,255,255,0.05)',
                                            '&:hover': { color: 'white', backgroundColor: 'rgba(255,255,255,0.15)' } 
                                        }}
                                    >
                                        <HelpOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Tabs value={activeTab} onChange={handleTabChange} aria-label="analysis results tabs" centered
                                sx={{ minHeight: '40px', '& .MuiTabs-flexContainer': { gap: '10px' }, '& .MuiTabs-indicator': { display: 'none' } }}>
                                {resultTasksInOrder.map((taskKey) => {
                                    const isMetaTaskTab = checkIsMetaTask(taskKey);
                                    
                                    // Fetch the tooltip description from translation file, fallback to YAML config
                                    const tooltipText = t(`verificationPage.scoreDefinitions.${taskKey}`, { 
                                        defaultValue: tasksConfig?.[taskKey]?.description || '' 
                                    });

                                    return (
                                        <Tab 
                                            key={taskKey}
                                            icon={isMetaTaskTab ? <img src="/static/vetraai_logo_grey.png" alt="Integrity Score" className="tab-logo-icon" /> : undefined}
                                            iconPosition="start"
                                            label={
                                                <Tooltip title={tooltipText} arrow placement="top" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <span>{getTaskDisplayName(taskKey, t)}</span>
                                                    </Box>
                                                </Tooltip>
                                            }
                                            className={`results-tab ${isMetaTaskTab ? 'meta-task-tab' : ''}`}
                                            sx={{ 
                                                minHeight: '38px', 
                                                padding: '6px 16px', 
                                                borderRadius: '8px !important', 
                                                overflow: 'hidden',
                                                color: 'rgba(255, 255, 255, 0.7)', 
                                                border: '1px solid rgba(255, 255, 255, 0.1)', 
                                                backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                                                textTransform: 'none', 
                                                fontSize: '0.9rem', 
                                                fontWeight: 600, 
                                                transition: 'all 0.3s ease', 
                                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }, 
                                                '&.Mui-selected': { color: '#0B1723', backgroundColor: 'white' }, 
                                            }} 
                                        />
                                    );
                                })}
                            </Tabs>
                        </motion.div>

                        <motion.div variants={containerVariants} className="report-layout-grid">
                            <div className="top-row-layout">
                                <motion.div variants={containerVariants} className="left-column-stack">
                                    <motion.div variants={itemVariants} className="content-preview-card glass-card-v2">
                                        <h3>{t('verificationPage.contentInfoSection.preview')}</h3>
                                        <Tooltip
                                            title={
                                                contentDetails.content_type === 'text' && contentDetails.content_preview.length > 150-10
                                                    ? t('verificationPage.previewTooltips.text')
                                                    : contentDetails.content_type === 'image'
                                                        ? t('verificationPage.previewTooltips.image')
                                                        : contentDetails.content_type === 'video'
                                                            ? t('verificationPage.previewTooltips.video')
                                                            : ""
                                            }
                                            arrow
                                            placement="top"
                                        >
                                            <div className="preview-media-container">
                                                {(contentDetails.content_type === 'video' || contentDetails.content_type === 'image') ?
                                                    (isValidPreview(contentDetails.content_preview) ?
                                                        <img src={contentDetails.content_preview} alt="Content Preview" className="preview-media" /> :
                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: '150px', backgroundColor: 'rgba(0,0,0,0.2)', color: 'rgba(255,255,255,0.4)', borderRadius: '8px' }}>
                                                            {contentDetails.content_type === 'video' ? <VideocamIcon style={{ fontSize: '3rem', opacity: 0.7, marginBottom: '8px' }} /> : <ImageIcon style={{ fontSize: '3rem', opacity: 0.7, marginBottom: '8px' }} />}
                                                            <span style={{ fontSize: '0.85rem', fontStyle: 'italic' }}>{t('historyItem.preview.noPreview', 'Preview not available')}</span>
                                                        </div>
                                                    ) :
                                                    (<p className="text-snippet-value">{`${contentDetails.content_preview}${contentDetails.content_preview.length > 150-10 ? '...' : ''}`}</p>)
                                                }
                                            </div>
                                        </Tooltip>
                                        {activeMetadata && (
                                            <div className="content-metadata-container">
                                                {/* 1. Description */}
                                                {activeMetadata.summary && (
                                                    <div className="metadata-description-block">
                                                        <span className="metadata-label">{t('contentMetadata.descriptionLabel')}</span>
                                                        <p className="metadata-summary-text">
                                                            {isCheckingDescriptionCache ? (
                                                                <span style={{ opacity: 0.5, fontStyle: 'italic' }}>
                                                                    {t('resultDisplay.checkingTranslation')}...
                                                                </span>
                                                            ) : (
                                                                `"${translatedDescription || activeMetadata.summary}"`
                                                            )}
                                                            
                                                            {activeMetadata.category && (
                                                                <span className="metadata-category-tag">
                                                                    &nbsp;&nbsp;({t(`contentMetadata.inclusion.${activeMetadata.category}`, { 
                                                                        defaultValue: activeMetadata.category 
                                                                    })})
                                                                </span>
                                                            )}
                                                        </p>
                                                    </div>
                                                )}

                                                {/* 2. Safety Flags */}
                                                {activeMetadata.safety_flags && 
                                                activeMetadata.safety_flags.length > 0 && (
                                                    <div className="metadata-safety-alert">
                                                        <ReportProblemOutlinedIcon fontSize="small" />
                                                        <div className="safety-flag-list">
                                                            {/* Use localized prefix */}
                                                            <strong>{t('metaScores.safetyAlertPrefix')}</strong>&nbsp;
                                                            {activeMetadata.safety_flags.map((flagKey, index) => (
                                                                <span key={index}>
                                                                    {t(`contentMetadata.exclusion.${flagKey}`, { defaultValue: flagKey })}
                                                                    {index < activeMetadata.safety_flags.length - 1 ? ', ' : ''}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </motion.div>

                                    <motion.div variants={itemVariants} className="context-card glass-card-v2">
                                        <h3>{t('verificationPage.contentInfoSection.contextTitle')}</h3>
                                        {hasContext ? (
                                            <>
                                                {rawContext.source_url && (
                                                    <div className="context-row">
                                                        <span className="context-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            <LinkIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.3)' }} />
                                                            {t('verificationPage.contextLabels.sourceUrl')}
                                                        </span>
                                                        <span className="context-value">
                                                            <a href={rawContext.source_url} target="_blank" rel="noopener noreferrer">{rawContext.source_url}</a>
                                                        </span>
                                                    </div>
                                                )}
                                                {rawContext.platform && (
                                                    <div className="context-row">
                                                        <span className="context-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            <PublicIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.3)' }} />
                                                            {t('verificationPage.contextLabels.platform')}
                                                        </span>
                                                        <span className="context-value">{getPlatformDisplayName(rawContext.platform, t)}</span>
                                                    </div>
                                                )}
                                                {rawContext.user_notes && (
                                                    <div className="context-row">
                                                        <span className="context-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            <NotesIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.3)' }} />
                                                            {t('verificationPage.contextLabels.additionalNotes')}
                                                        </span>
                                                        <span className="context-value">{rawContext.user_notes}</span>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="no-context-message">{t('verificationPage.contentInfoSection.noContext')}</div>
                                        )}
                                    </motion.div>

                                    {shouldShowShareCard && (
                                        <motion.div variants={itemVariants} className="share-card glass-card-v2">
                                            <h3>{t('verificationPage.share.title')}</h3>
                                            <div className="share-content-horizontal">
                                                <div className="qr-code-wrapper">
                                                    <div className="qr-code-and-label">
                                                        <div className="qr-image-container" style={{ minHeight: '100px', minWidth: '150px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            {isQrLoading && <CircularProgress className="qr-spinner" size={40} aria-label="Loading QR code" />}
                                                            <img src={qrCodeUrl} alt="Verification QR Code" onLoad={() => setIsQrLoading(false)} onError={() => setIsQrLoading(false)} style={{ display: isQrLoading ? 'none' : 'block' }} />
                                                        </div>
                                                        <div className="qr-code-label" style={{ visibility: isQrLoading ? 'hidden' : 'visible' }}>
                                                            <QrCodeScannerIcon />
                                                            <span>{t('verificationPage.share.scanLabel')}</span>
                                                        </div>
                                                    </div>
                                                    <div style={{ display: 'flex', gap: '15px', width: '100%', marginTop: '10px' }}>
                                                        <Button 
                                                            variant="text" 
                                                            startIcon={<ContentCopyIcon />} 
                                                            onClick={handleCopyLink} 
                                                            className="copy-link-button-v2" 
                                                            disabled={copySuccess}
                                                            sx={{ 
                                                                flex: 1, 
                                                                padding: '6px 4px !important', 
                                                                fontSize: '0.75rem !important', 
                                                                minWidth: '0 !important',
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                        >
                                                            {copySuccess ? t('verificationPage.share.copyButtonSuccess') : t('verificationPage.share.copyButton')}
                                                        </Button>
                                                        <Button 
                                                            variant="text" 
                                                            startIcon={isDownloadingQr ? <CircularProgress size={16} color="inherit" aria-hidden="true" /> : <FileDownloadIcon />}
                                                            onClick={handleDownloadQr} 
                                                            disabled={isDownloadingQr}
                                                            className="copy-link-button-v2"
                                                            sx={{ 
                                                                flex: 1, 
                                                                padding: '8px 12px !important',
                                                                fontSize: '0.75rem !important', 
                                                                minWidth: '0 !important',
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                        >
                                                            {isDownloadingQr ? t('verificationPage.share.isDownloading') : t('verificationPage.share.downloadButton')}
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </motion.div>
                                <motion.div variants={itemVariants} className="right-column-fill">
                                    <div className="analysis-summary-card glass-card-v2">
                                        {activeResultData && activeResultData.score != null && (
                                            <ScoreDonut 
                                                size={120} 
                                                riskScore={activeResultData.score} 
                                                scoreType={activeTaskKey} 
                                                subScores={subScores} 
                                                t={t} 
                                            />
                                        )}
                                        <div className="metadata-list">
                                            {metadataItems.map(item => (
                                                <div key={item.label} className="metadata-item" style={{ width: '100%' }}>
                                                    <div className="metadata-icon">{item.icon}</div>
                                                    
                                                    <div className="metadata-text" style={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <div style={{ minWidth: 0, paddingRight: '8px' }}>
                                                            <span className="metadata-label" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                {item.label}
                                                                {item.infoTooltip && (
                                                                    <Tooltip title={item.infoTooltip} placement="top" arrow>
                                                                        <InfoOutlinedIcon sx={{ fontSize: '0.8rem', cursor: 'help', color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'white' } }} />
                                                                    </Tooltip>
                                                                )}
                                                            </span>
                                                            <strong 
                                                                className={`metadata-value ${item.isHash ? 'hash-value' : ''}`}
                                                                style={item.dimmed ? { color: 'rgba(255,255,255,0.4)', fontStyle: 'italic', fontWeight: 'normal' } : {}}
                                                            >
                                                                {item.isLink ? (
                                                                    <a href={item.copyText} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
                                                                        {item.value}
                                                                    </a>
                                                                ) : (
                                                                    item.value
                                                                )}
                                                            </strong>
                                                        </div>
                                                        
                                                        {/* RENDER COPY BUTTON IF APPLICABLE */}
                                                        {item.copyable && item.copyText && (
                                                            <Tooltip title={copiedItemId === item.label ? t('verificationPage.share.copyButtonSuccess') : (item.copyTooltip || "Copy")} placement="top">
                                                                <IconButton 
                                                                    size="small" 
                                                                    onClick={() => {
                                                                        navigator.clipboard.writeText(item.copyText);
                                                                        setCopiedItemId(item.label);
                                                                        setTimeout(() => setCopiedItemId(null), 2000);
                                                                    }} 
                                                                    sx={{ 
                                                                        color: copiedItemId === item.label ? '#4CAF50' : 'rgba(255,255,255,0.4)', 
                                                                        '&:hover': { color: 'white' },
                                                                        flexShrink: 0
                                                                    }}
                                                                >
                                                                    {copiedItemId === item.label ? <CheckCircleIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </motion.div>
                            </div>

                            {/* COMBINED EXPLANATION & REGIONS CARD */}
                            {(shouldShowExplanation || shouldShowRegions) && (
                                <motion.div variants={itemVariants} className="bottom-row-layout">
                                    <div className="explanation-card glass-card-v2">

                                        {/* Dynamic Title */}
                                        <h3>{cardTitle}</h3>

                                        {/* Explanation Text */}
                                        {shouldShowExplanation && (
                                            isCheckingCache ? (
                                                <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}><CircularProgress size={24} color="inherit" aria-hidden="true" aria-label="Checking translation cache" /></div>
                                            ) : activeResultData ? (
                                                isMetaTask ? (
                                                    <StructuredExplanation 
                                                        data={activeResultData.explanation || activeResultData.explanation_text}
                                                        score={activeResultData.score} 
                                                    />
                                                ) : (
                                                    // --- LOGIC FOR TEXT with CLEANING ---
                                                    rawExplanation ? (
                                                        <ReactMarkdown 
                                                            remarkPlugins={[remarkGfm]}
                                                            components={{
                                                                // Consistent styling components from ExplanationModal
                                                                strong: ({node, ...props}) => <span style={{color: 'white', fontWeight: '700'}} {...props} />,
                                                                em: ({node, ...props}) => <span style={{color: 'white', fontWeight: '700', fontStyle: 'normal'}} {...props} />
                                                            }}
                                                        >
                                                            {DOMPurify.sanitize(cleanMarkdown(translatedExplanation || rawExplanation))}
                                                        </ReactMarkdown>
                                                    ) : (
                                                        <p className="explanation-placeholder">
                                                            {activeResultData.explanation_generated === false
                                                                ? t('verificationPage.tooltips.explanationDisabled')
                                                                : t('verificationPage.analysisSection.noHistory')
                                                            }
                                                        </p>
                                                    )
                                                )
                                            ) : (
                                                <p>{t('verificationPage.analysisSection.noHistory')}</p>
                                            )
                                        )}

                                        {/* Buttons Container */}
                                        {shouldShowRegions && (
                                            <Box sx={{ mt: shouldShowExplanation ? 2 : 0, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {isMetaTask ? (
                                                    <>
                                                        {analysisResults['ai_detection'] && <InspectButton taskKey="ai_detection" />}
                                                        {analysisResults['fake_news_detection'] && <InspectButton taskKey="fake_news_detection" />}
                                                    </>
                                                ) : (
                                                    <InspectButton taskKey={activeTaskKey} />
                                                )}
                                            </Box>
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </main>
            <Footer {...footerProps} />
            <HeatmapModal isOpen={isHeatmapModalOpen} onClose={() => setIsHeatmapModalOpen(false)} {...heatmapModalData} />
            <HistoryTextInspectionModal isOpen={isTextInspectionModalOpen} onClose={() => setIsTextInspectionModalOpen(false)} {...textInspectionModalData} content={contentDetails?.content_preview} />

            {/* License Modal for Activation/Extension */}
            <LicenseModal 
                isOpen={isLicenseModalOpen}
                onClose={() => setIsLicenseModalOpen(false)}
                onConfirm={handleLicensePayment}
                isPaying={isPayingLicense} 
                isLoading={false}
                currentLicense={licenseInfo}
                contentType={contentDetails?.content_type || 'image'}
                pricingConfig={pricingConfig}
                currencyRates={currencyRates}
                selectedCurrency="USD"
                mode={licenseInfo?.status === 'active' ? 'extend' : 'activate'}
            />

            {/* INFO MODAL OVERLAY */}
            {isInfoModalOpen && (
                <div className="info-modal-overlay" onClick={() => setIsInfoModalOpen(false)}>
                    <Paper 
                        elevation={6}
                        className="info-modal-paper"
                        onClick={(e) => e.stopPropagation()} // Prevent click-away close when clicking inside
                    >
                        <IconButton 
                            onClick={() => setIsInfoModalOpen(false)}
                            sx={{ position: 'absolute', top: 12, right: 12, color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }}
                        >
                            <CloseIcon />
                        </IconButton>
                        
                        <Typography variant="h5" sx={{ mb: 1, fontWeight: 'bold', color: 'white', pr: 4 }}>
                            {t('verificationPage.infoModal.title')}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 4, color: 'rgba(255,255,255,0.7)' }}>
                            {t('verificationPage.infoModal.subtitle')}
                        </Typography>

                        {/* Expandable Overview Section */}
                        <Accordion defaultExpanded sx={{ backgroundColor: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', mb: 2, borderRadius: '8px !important', '&:before': { display: 'none' } }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{color: 'white'}}/>}>
                                <Typography fontWeight="bold">{t('verificationPage.infoModal.overviewTitle')}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.6 }}>
                                    <Trans 
                                        i18nKey="verificationPage.infoModal.overviewDynamic" 
                                        values={{ contentType: localizedContentType, tasks: activeBaseTaskNames }}
                                        components={{ 1: <strong style={{ color: 'white' }} />, 3: <strong style={{ color: 'white' }} /> }}
                                    />
                                    {metaTasks.length > 0 && (
                                        <Trans 
                                            i18nKey="verificationPage.infoModal.overviewMeta" 
                                            values={{ metaTasks: activeMetaTaskNames }}
                                            components={{ 1: <strong style={{ color: 'white' }} /> }}
                                        />
                                    )}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>

                        {/* Expandable Detailed Section */}
                        <Accordion sx={{ backgroundColor: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{color: 'white'}}/>}>
                                <Typography fontWeight="bold">{t('verificationPage.infoModal.detailedTitle')}</Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0, px: 2, pb: 2 }}>
                                <div className="scrollable-accordion-content">
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                                        {t('verificationPage.infoModal.detailedText')}
                                    </Typography>
                                </div>
                            </AccordionDetails>
                        </Accordion>
                    </Paper>
                </div>
            )}

        </div>
    );
}

export default VerificationPage;